<template>
  <div
    class="appointment-card"
    :class="{ selected: isSelected }"
    @click="handleClick"
  >
    <div class="appointment-header">
      <div class="appointment-header-content">
        <div class="clinic-name">{{ reservation.summary.clinicName }}</div>
        <div class="patient-name">{{ reservation.summary.patientName || '-' }}</div>
        <div class="appointment-summary">
          <div class="summary-item">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <span class="summary-value">{{ formattedDate }} {{ reservation.summary.slotText }}</span>
          </div>
          <span class="appointment-type">{{ reservation.summary.typeName }}</span>
        </div>
      </div>
      <button class="expand-btn" :class="{ expanded: isExpanded }">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
    </div>
    <div class="appointment-details" :class="{ show: isExpanded }">
      <div class="appointment-info">
        <div class="info-row" v-for="(item, idx) in detailFields" :key="`${item.label}-${idx}`">
          <span class="info-label">{{ item.label }}</span>
          <span class="info-value">{{ item.label === '費用' && item.value !== '-' ? `${item.value} 元` : item.value }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReservationCard',
  props: {
    reservation: {
      type: Object,
      required: true
    },
    isExpanded: {
      type: Boolean,
      default: false
    },
    isSelected: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    formattedDate() {
      const date = new Date(this.reservation.summary.visitDate)
      if (isNaN(date.getTime())) return this.reservation.summary.visitDate

      const month = date.getMonth() + 1
      const day = date.getDate()
      const weekday = ['日', '一', '二', '三', '四', '五', '六'][date.getDay()]

      return `${month}/${day} (${weekday})`
    },
    statusClass() {
      const statusMap = {
        confirmed: 'confirmed',
        pending: 'pending',
        cancelled: 'cancelled'
      }
      return statusMap[this.reservation.status] || ''
    },
    statusText() {
      const statusMap = {
        confirmed: '已確認',
        pending: '待確認',
        cancelled: '已取消'
      }
      return statusMap[this.reservation.status] || this.reservation.status
    },
    detailFields() {
      const summary = this.reservation && this.reservation.summary ? this.reservation.summary : {}
      const typeName = summary.typeName || ''
      const baseFields = []

      if (typeName === '一般看診') {
        baseFields.push(
          this.buildDetailField('號碼', '預計看診號'),
          this.buildDetailField('就診科別', '科別'),
          this.buildDetailField('看診醫師', '看診醫師')
        )
      }

      if (typeName === '體檢/健檢' || typeName === '疫苗') {
        baseFields.push(
          this.buildDetailField('號碼', '預計看診號'),
          this.buildDetailField(`${typeName} 種類`, '就診細項')
        )

        if (this.hasInfoBlockValue('加值項目')) {
          baseFields.push(this.buildDetailField('加值項目', '加值項目', true))
        }

        if (this.hasInfoBlockValue('金額')) {
          baseFields.push(this.buildDetailField('費用', '金額'))
        }
      }

      return baseFields
    }
  },
  methods: {
    handleClick() {
      this.$emit('click', this.reservation)
    },
    buildDetailField(label, title, includeAll = false) {
      return {
        label,
        value: this.getInfoBlockDisplayValue(title, includeAll)
      }
    },
    getInfoBlockItems(title) {
      const summary = this.reservation && this.reservation.summary ? this.reservation.summary : {}
      const infoBlock = Array.isArray(summary.infoBlock) ? summary.infoBlock : []
      return infoBlock.filter((item) => item && item.title === title)
    },
    getInfoBlockRawValue(item) {
      if (!item) return ''
      if (item.value !== undefined && item.value !== null) return item.value
      if (item.text !== undefined && item.text !== null) return item.text
      return ''
    },
    getInfoBlockRawValues(title) {
      return this.getInfoBlockItems(title).map((item) => this.getInfoBlockRawValue(item))
    },
    hasInfoBlockValue(title) {
      return this.getInfoBlockRawValues(title).some((rawValue) => String(rawValue).trim() !== '')
    },
    getInfoBlockDisplayValue(title, includeAll = false) {
      const rawValues = this.getInfoBlockRawValues(title)
      const formattedValues = rawValues
        .map((rawValue) => String(rawValue).trim())
        .filter((value) => value !== '')

      if (formattedValues.length === 0) return '-'
      if (includeAll) return formattedValues.join('\n')
      return formattedValues[0]
    }
  }
}
</script>

<style lang="scss" scoped>
.appointment-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border: 2px solid #e9ecef;
  margin-bottom: 16px;
  transition: all 0.3s;
  position: relative;
  cursor: pointer;

  &:hover {
    border-color: #2a9d8f;
    box-shadow: 0 4px 12px rgba(42, 157, 143, 0.15);
  }

  &.selected {
    background: #e8f5f3;
    border-color: #2a9d8f;
  }
}

.appointment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  position: relative;
}

.appointment-header-content {
  flex: 1;
  min-width: 0;
}

.patient-name {
  font-size: 26px;
  font-weight: bold;
  color: #333;
  margin-bottom: 4px;
}

.clinic-name {
  font-size: 14px;
  color: #666;
  margin-bottom: 12px;
  font-weight: bold;
}

.appointment-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  color: #666;
  font-weight: 600;

  svg {
    width: 20px;
    height: 20px;
    color: #2a9d8f;
    flex-shrink: 0;
  }
}

.summary-value {
  color: #333;
}

.appointment-type {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 700;
  background: #e8f5f3;
  color: #207868;
}

.expand-btn {
  position: absolute;
  top: 0;
  right: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    color: #2a9d8f;
  }

  &.expanded svg {
    transform: rotate(180deg);
  }
}

.appointment-details {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease, margin-top 0.3s ease;
  opacity: 0;
  width: 100%;

  &.show {
    max-height: 500px;
    opacity: 1;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 2px solid #e9ecef;
    overflow: hidden;
  }
}

.appointment-info {
  display: grid;
  width: 100%;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  word-break: break-word;

  &:not(:last-child) {
    border-bottom: 1px solid #d9d9d9;
    margin-bottom: 12px;
    padding-bottom: 12px;
  }
}

.info-label {
  font-size: 20px;
  color: #666;
  font-weight: 500;
  flex-shrink: 0;
  margin-right: 12px;
}

.info-value {
  font-size: 20px;
  color: #333;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
  white-space: pre-line;

  &.status {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 16px;
    font-weight: 700;

    &.confirmed {
      background: #e8f5f3;
      color: #207868;
    }

    &.pending {
      background: #fef3e8;
      color: #f59e0b;
    }

    &.cancelled {
      background: #fce4ec;
      color: #dc2626;
    }
  }
}

@media (max-width: 767px) {
  .appointment-card {
    padding: 16px;
  }

  .patient-name {
    font-size: 20px;
  }

  .clinic-name {
    font-size: 20px;
  }

  .summary-item {
    font-size: 16px;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .info-value {
    text-align: left;
  }
}
</style>
